import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, FileSpreadsheet, Loader2 } from 'lucide-react';
import { apiFetch, getApiBase } from './apiBase';
import FilterToolbar, { FilterForm } from './FilterToolbar';

const EMPTY_FILTERS = { person: '', q: '', cols: {} };

function compactCols(cols = {}) {
  const out = {};
  for (const [k, v] of Object.entries(cols)) {
    const t = String(v || '').trim();
    if (t) out[k] = t;
  }
  return out;
}

function filtersActive(f) {
  if (!f) return false;
  if (String(f.person || '').trim() || String(f.q || '').trim()) return true;
  return Object.values(f.cols || {}).some((v) => String(v || '').trim());
}

export default function CatalogReportTable({
  rowsUrl,
  exportUrl,
  title = 'Results',
  refreshKey = 0,
}) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');

  const hasActiveFilters = filtersActive(applied);

  const loadRows = useCallback(
    async (active) => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams();
        if (active.person) params.set('person', active.person);
        if (active.q) params.set('q', active.q);
        const cols = compactCols(active.cols);
        if (Object.keys(cols).length) params.set('cols', JSON.stringify(cols));
        params.set('limit', '500');
        const payload = await apiFetch(`${rowsUrl}?${params.toString()}`);
        setData(payload);
      } catch (e) {
        setError(e.message || 'Failed to load rows');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [rowsUrl]
  );

  useEffect(() => {
    loadRows(applied);
  }, [loadRows, applied, refreshKey]);

  const applyFilters = () =>
    setApplied({
      person: filters.person,
      q: filters.q,
      cols: compactCols(filters.cols),
    });

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
  };

  const setColFilter = (col, value) =>
    setFilters((f) => ({ ...f, cols: { ...f.cols, [col]: value } }));

  const downloadExcel = async () => {
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams();
      if (applied.person) params.set('person', applied.person);
      if (applied.q) params.set('q', applied.q);
      const cols = compactCols(applied.cols);
      if (Object.keys(cols).length) params.set('cols', JSON.stringify(cols));
      const res = await fetch(`${getApiBase()}${exportUrl}?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || 'Export failed. Try again.');
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const match = /filename="([^"]+)"/i.exec(cd);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = match?.[1] || 'report.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  if (!data && !loading && !error) return null;
  if (data && data.total === 0 && !hasActiveFilters && !loading) return null;

  const columns = data?.columns || [];
  const summaryText =
    data?.total != null
      ? `${data.total.toLocaleString()} row(s)${hasActiveFilters ? ' matching filters' : ''}${
          data.columnCount ? ` · ${data.columnCount} columns` : ''
        }${data.total > (data.rows?.length || 0) ? ` · showing first ${data.rows.length}` : ''}`
      : 'Loading…';

  return (
    <div className="mt-4">
      <div className="overflow-hidden rounded-lg border border-slate-700/80">
        <div className="flex items-center justify-between gap-3 px-3 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            aria-expanded={expanded}
          >
            <span className="text-sm font-semibold text-slate-200">{title}</span>
            <span className="truncate text-xs text-slate-500">{summaryText}</span>
          </button>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="btn-secondary shrink-0 !p-1.5"
            title={expanded ? 'Minimize results' : 'Expand results'}
          >
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>

        {expanded && (
          <FilterForm
            onApply={applyFilters}
            disabled={loading}
            className="space-y-3 border-t border-slate-700/80 px-3 pb-3 pt-3"
          >
            <FilterToolbar
              onClear={clearFilters}
              hasActiveFilters={hasActiveFilters}
              disabled={loading}
              extra={
                <button
                  type="button"
                  onClick={downloadExcel}
                  disabled={exporting || !data?.total}
                  className="btn-secondary py-1.5 text-sm"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileSpreadsheet className="h-4 w-4" />
                  )}
                  Excel
                </button>
              }
            >
              <input
                value={filters.person}
                onChange={(e) => setFilters((f) => ({ ...f, person: e.target.value }))}
                placeholder="Person number…"
                className="filter-input text-sm"
              />
              <input
                value={filters.q}
                onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                placeholder="Search all columns…"
                className="filter-input min-w-[12rem] text-sm"
              />
            </FilterToolbar>

            {exportError && <p className="text-xs text-rose-400">{exportError}</p>}
            {error && <p className="alert-error">{error}</p>}

            <div className="overflow-hidden rounded-lg border border-slate-700/80">
              <div className="max-h-[min(420px,50vh)] overflow-auto">
                <table className="data-table w-full text-xs">
                  <thead className="sticky top-0 z-10">
                    <tr>
                      {columns.map((col) => (
                        <th key={col} className="whitespace-nowrap px-2 py-2 text-left font-medium">
                          {col}
                        </th>
                      ))}
                    </tr>
                    <tr className="border-b border-slate-700 bg-slate-800/50">
                      {columns.map((col) => (
                        <th key={`${col}-filter`} className="px-2 py-1.5">
                          <input
                            value={filters.cols?.[col] || ''}
                            onChange={(e) => setColFilter(col, e.target.value)}
                            placeholder="Filter…"
                            className="filter-input min-w-[5.5rem]"
                            aria-label={`Filter ${col}`}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && !data?.rows?.length ? (
                      <tr>
                        <td colSpan={columns.length || 1} className="px-3 py-8 text-center text-slate-500">
                          <Loader2 className="mx-auto h-5 w-5 animate-spin" />
                        </td>
                      </tr>
                    ) : data?.rows?.length ? (
                      data.rows.map((row, idx) => (
                        <tr key={idx}>
                          {columns.map((col) => (
                            <td key={col} className="whitespace-nowrap px-2 py-1.5 text-slate-200">
                              {row[col] || '—'}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={columns.length || 1} className="px-3 py-6 text-center text-slate-500">
                          No rows match the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </FilterForm>
        )}
      </div>
    </div>
  );
}
